// §7.6 — quotas, persistence and eviction (R4).
//
// Three things, all small: the `storage.estimate()` readout Settings shows, the 90 % warning
// (writes still succeed — the warning is a warning, not a wall), and the once-per-session guest
// banner whose wording PLAN binds.
import type { Bus } from '../bus';
import { QUOTA_CHECK_INTERVAL_MS, QUOTA_WARN_RATIO } from '@/config';

export interface QuotaReading {
  usageBytes: number;
  quotaBytes: number;
  /** 0…1. Zero when the browser reports no quota rather than pretending the disk is full. */
  ratio: number;
  /** At or over `QUOTA_WARN_RATIO`. */
  over: boolean;
}

export interface QuotaService {
  /** The Settings readout (§7.6). Never throttled — it is a deliberate act. */
  read(): Promise<QuotaReading>;
  /**
   * Called after a write. Throttled, and fires `fs:quotaWarning` **at most once per session**
   * — a warning that repeats on every save is a warning nobody reads.
   */
  check(): Promise<QuotaReading | null>;
  warned(): boolean;
  /** §7.6 — shown once per session to a guest, then never again until a reload. */
  guestBannerVisible(): boolean;
  dismissGuestBanner(): void;
  /** Subscribe to banner visibility changes; returns the unsubscribe. */
  onBannerChange(listener: () => void): () => void;
}

export interface QuotaOptions {
  estimate: () => Promise<{ usage: number; quota: number }>;
  /** §8.3 — the banner is for guests only. */
  isGuest: () => boolean;
  bus?: Bus;
  now?: () => number;
  /** Overridden in tests. */
  checkIntervalMs?: number;
  warnRatio?: number;
}

export function createQuotaService(options: QuotaOptions): QuotaService {
  const now = options.now ?? (() => Date.now());
  const interval = options.checkIntervalMs ?? QUOTA_CHECK_INTERVAL_MS;
  const warnRatio = options.warnRatio ?? QUOTA_WARN_RATIO;

  let hasWarned = false;
  let lastCheck = Number.NEGATIVE_INFINITY;
  let bannerDismissed = false;
  const listeners = new Set<() => void>();

  async function read(): Promise<QuotaReading> {
    const { usage, quota } = await options.estimate();
    const ratio = quota > 0 ? usage / quota : 0;
    return { usageBytes: usage, quotaBytes: quota, ratio, over: ratio >= warnRatio };
  }

  return {
    read,

    async check() {
      // Once the warning has fired there is nothing left to discover this session, and
      // `estimate()` is not free enough to run on every keystroke-save.
      if (hasWarned) return null;
      const at = now();
      if (at - lastCheck < interval) return null;
      lastCheck = at;

      const reading = await read();
      if (!reading.over) return reading;
      hasWarned = true;
      options.bus?.emit('fs:quotaWarning', {
        usedBytes: reading.usageBytes,
        quotaBytes: reading.quotaBytes,
      });
      return reading;
    },

    warned: () => hasWarned,

    guestBannerVisible: () => options.isGuest() && !bannerDismissed,

    dismissGuestBanner() {
      if (bannerDismissed) return;
      bannerDismissed = true;
      for (const listener of [...listeners]) listener();
    },

    onBannerChange(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
